/* cs-p02-collection.js
 * P-02 系列頁的 JS 強化。沒有這支檔案時，篩選與排序是 GET 表單、手機抽屜是 :target，照樣能用。
 * 做的事：
 *  1. 桌面：勾選／排序立即套用；價錢輸入 debounce 500ms。用 Section Rendering API（目前網址參數 + &section_id=）抓回 HTML，
 *     只替換 [data-cs-swap] 的四塊（toolbar／side／grid／drawer），history.replaceState 更新網址。網址保留當頁範本的 view（root 的 data-cs-view；預設系列頁是空字串＝一律不帶 view，只有找不到根元素才退回 p02）。
 *  2. 手機抽屜：開／關、焦點鎖、Esc、關閉後焦點回到開啟鍵；抽屜內改動只更新底鍵「查看 N 件」的 N，按下才套用。
 *     [第二十五輪提案] 不按「查看」就關（關閉鍵、Esc、拉到桌面寬度）時，未套用的改動丟掉，回到伺服器渲染的已套用狀態（見 revertDrawer）。
 *  4. 用途組（tag）：勾選 cs_use 時把路徑換成 /collections/<系列>/<tag handle>（其他篩選參數與當頁的 view 照留），section rendering 也用該路徑；已選小標的 × 只移除該 tag。
 *  3. <cs-p02-price>：兩個數字輸入框＋雙滑桿雙向同步（金額單位是元，與 live 相同）。
 *  6. 第三十一輪丙「看更多」：點 [data-cs-p02-more] 用 section rendering 抓下一頁、去重後接在目前的商品格後面（見 loadMore）；排序「精選」的值是空字串，paramsFromForm 會略過，網址不帶 sort_by。
 *  7. 第三十一輪第二批「系列內搜尋」：側欄與抽屜各一個 [data-cs-p02-q]（沒有 name，不進篩選參數），共用同一個關鍵字（見 init 裡「系列內搜尋」那一段）。只過濾已載入的卡，不打伺服器（不呼叫 /search/suggest）。
 *  5. 審查第一輪（2026-10-01）：S-B7 抽屜「查看 N 件」一定套用（見 applyFromDrawer）；S-C7 浮層記號第二次量測不覆寫成 0；S-C4 各組標題旁的「已選 N」在抽屜／側欄裡隨勾選即時更新。
 */
(function () {
  'use strict';
  if (window.__csP02Collection) { return; }
  window.__csP02Collection = true;

  var SWAP = ['toolbar', 'side', 'grid', 'drawer'];
  var DESKTOP = window.matchMedia('(min-width: 990px)');

  /* ------------------------------------------------------------ 價錢範圍 */
  class CsP02Price extends HTMLElement {
    connectedCallback() {
      if (this._csInit) { return; }
      this.lowerNum = this.querySelector('[data-cs-price-lower]');
      this.upperNum = this.querySelector('[data-cs-price-upper]');
      this.lowerRng = this.querySelector('[data-cs-range-lower]');
      this.upperRng = this.querySelector('[data-cs-range-upper]');
      this.slider = this.querySelector('.cs-p02-price__slider');
      if (!this.lowerNum || !this.upperNum || !this.lowerRng || !this.upperRng) { return; }
      this._csInit = true;
      this.min = Number(this.dataset.min || 0);
      this.max = Number(this.dataset.max || 0);
      this.lowerRng.addEventListener('input', () => this.fromRange('lower'));
      this.upperRng.addEventListener('input', () => this.fromRange('upper'));
      this.lowerNum.addEventListener('input', () => this.fromNumbers());
      this.upperNum.addEventListener('input', () => this.fromNumbers());
      // r40 Codex e：換格移除輸入框時 Chrome 會送 change；那不是客人收尾，不可以拿舊表單的上下限去夾最新輸入（root 換格期間帶 data-cs-swapping）
      this.lowerNum.addEventListener('change', () => { if (!this.closest('[data-cs-swapping]')) { this.normalise('lower'); } });
      this.upperNum.addEventListener('change', () => { if (!this.closest('[data-cs-swapping]')) { this.normalise('upper'); } });
      this.fromNumbers();
    }
    num(v, fallback) {
      if (v === '' || v === null || v === undefined) { return fallback; }
      const n = Number(v);
      return isFinite(n) ? n : fallback;
    }
    clamp(v) { return Math.min(this.max, Math.max(this.min, v)); }
    paint(lo, hi) {
      const span = (this.max - this.min) || 1;
      const a = ((lo - this.min) / span) * 100;
      const b = ((hi - this.min) / span) * 100;
      if (this.slider) {
        this.slider.style.setProperty('--lo', a + '%');
        this.slider.style.setProperty('--hi', b + '%');
      }
      this.lowerRng.style.zIndex = a > 50 ? '3' : '1';
    }
    fromRange(side) {
      let lo = this.clamp(this.num(this.lowerRng.value, this.min));
      let hi = this.clamp(this.num(this.upperRng.value, this.max));
      if (side === 'lower' && lo > hi) { lo = hi; }
      if (side === 'upper' && hi < lo) { hi = lo; }
      this.lowerRng.value = lo;
      this.upperRng.value = hi;
      this.lowerNum.value = lo === this.min ? '' : lo;
      this.upperNum.value = hi === this.max ? '' : hi;
      this.paint(lo, hi);
    }
    fromNumbers() {
      let lo = this.clamp(this.num(this.lowerNum.value, this.min));
      let hi = this.clamp(this.num(this.upperNum.value, this.max));
      if (lo > hi) { lo = hi; }
      this.lowerRng.value = lo;
      this.upperRng.value = hi;
      this.paint(lo, hi);
    }
    normalise(side) {
      let lo = this.clamp(this.num(this.lowerNum.value, this.min));
      let hi = this.clamp(this.num(this.upperNum.value, this.max));
      if (this.lowerNum.value !== '' && this.upperNum.value !== '' && lo > hi) {
        // r41 T05：兩個框都有值而且倒置 → 對調（與 normalisePriceParams 的「上下限倒置 → 對調」同一個結果，失焦和 debounce 不再各說各話）
        const t = lo; lo = hi; hi = t;
      } else {
        if (side === 'lower' && lo > hi) { lo = hi; }
        if (side === 'upper' && hi < lo) { hi = lo; }
      }
      if (this.lowerNum.value !== '') { this.lowerNum.value = lo; }
      if (this.upperNum.value !== '') { this.upperNum.value = hi; }
      this.fromNumbers();
    }
    reset() {
      this.lowerNum.value = '';
      this.upperNum.value = '';
      this.fromNumbers();
    }
  }
  if (!customElements.get('cs-p02-price')) { customElements.define('cs-p02-price', CsP02Price); }

  /* ------------------------------------------------------------ 共用小工具 */
  /* 用途組（tag）：cs_use（勾選的用途 tag handle）與 cs_tag（其他已在路徑上的 tag）不進查詢字串，改由 resolveForm 組成 /collections/<系列>/<tag>+<tag> */
  function basePathOf(pathname) {
    var m = pathname.match(/^(.*\/collections\/[^\/]+)/);
    return m ? m[1] : pathname;
  }
  function tagsOfPath(pathname) {
    var rest = pathname.slice(basePathOf(pathname).length).replace(/^\//, '');
    return rest ? rest.split('+') : [];
  }
  function pathWithTags(tagsEncoded) {
    var base = basePathOf(window.location.pathname);
    return base + (tagsEncoded.length ? '/' + tagsEncoded.join('+') : '');
  }
  function decodeSafe(x) { try { return decodeURIComponent(x); } catch (e) { return x; } }
  /* Codex 第 15 條：組網址前先把價錢值正規化（觸控 pointerup 直接套用會繞過原生表單驗證，number input 的 min／max 不會擋）。
     規則：空字串或不是數字 → 不送出參數；負數 → 0；超過上限（<cs-p02-price data-max>）→ 上限；上下限倒置 → 對調。
     只改「要送出的網址參數」，不改輸入框的值（使用者還在打字時不能被改掉）。 */
  function normalisePriceParams(form, p) {
    form.querySelectorAll('cs-p02-price').forEach(function (el) {
      var lo = el.querySelector('[data-cs-price-lower]');
      var hi = el.querySelector('[data-cs-price-upper]');
      if (!lo || !hi || !lo.name || !hi.name) { return; }
      var max = Number(el.getAttribute('data-max') || 0);
      function read(name) {
        var raw = p.get(name);
        if (raw === null || String(raw).trim() === '') { return null; }
        var n = Number(raw);
        return isFinite(n) ? n : null;
      }
      function clampOne(n) {
        if (n === null) { return null; }
        n = Math.max(0, n);
        return max > 0 ? Math.min(max, n) : n;
      }
      var a = clampOne(read(lo.name));
      var b = clampOne(read(hi.name));
      if (a !== null && b !== null && a > b) { var t = a; a = b; b = t; }
      p.delete(lo.name);
      p.delete(hi.name);
      if (a !== null) { p.set(lo.name, String(a)); }
      if (b !== null) { p.set(hi.name, String(b)); }
    });
  }
  function csView() { var r = document.querySelector('[data-cs-p02][data-cs-view]'); return r ? (r.getAttribute('data-cs-view') || '') : 'p02'; }   // 當頁範本的 view（section 根元素 data-cs-view，可以是空字串＝預設系列頁）；找不到根元素才用 p02
  function setView(p) { var v = csView(); if (v) { p.set('view', v); } else { p.delete('view'); } }   // 空白＝不帶 view（r39 止血）
  function paramsFromForm(form, ext) {
    var p = new URLSearchParams();
    var add = function (v, k) {
      if (typeof v !== 'string' || v === '') { return; }
      if (k === 'cs_use' || k === 'cs_tag') { return; }
      p.append(k, v);
    };
    new FormData(form).forEach(add);
    // r40 Codex h：脫離 DOM 的舊表單，FormData 讀不到表單外以 form= 關聯的控制項（排序 select）；由呼叫端從現行表單補上，已有同名的不重複加
    if (ext) {
      ext.forEach(function (el) {
        if (!el.name || el.disabled || p.has(el.name)) { return; }
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) { return; }
        if (el.type === 'submit' || el.type === 'button' || el.tagName === 'BUTTON' || el.tagName === 'FIELDSET') { return; }
        add(el.value, el.name);
      });
    }
    normalisePriceParams(form, p);
    setView(p);
    p.delete('page');
    return p;
  }
  function hasOtherFilter(p) {
    var found = false;
    p.forEach(function (v, k) { if (k !== 'view' && k !== 'sort_by' && k !== 'page') { found = true; } });
    return found;
  }
  /* 表單 → { params, path }。resetMode（section 設定 use_reset）：選用途時清掉其他篩選；改其他篩選時清掉用途 */
  function resolveForm(form, useChanged, resetMode, ext) {
    var params = paramsFromForm(form, ext);
    var uses = [];
    var others = [];
    form.querySelectorAll('input[name="cs_use"]:checked').forEach(function (i) { uses.push(i.value); });
    form.querySelectorAll('input[name="cs_tag"]').forEach(function (i) { if (i.value) { others.push(i.value); } });
    if (resetMode) {
      var now = form.getAttribute('data-cs-use-now') || '';
      var changed = useChanged || (uses.join('+') !== now);
      if (uses.length && changed) {
        var keep = new URLSearchParams();
        setView(keep);
        var sb = params.get('sort_by');
        if (sb) { keep.set('sort_by', sb); }
        params = keep;
      } else if (uses.length && hasOtherFilter(params)) {
        uses = [];
      }
    }
    var tags = others.concat(uses).map(encodeURIComponent);
    return { params: params, path: pathWithTags(tags) };
  }
  function paramsFromLink(a) {
    var u = new URL(a.getAttribute('href'), window.location.href);
    var p = new URLSearchParams(u.search);
    setView(p);
    p.delete('page');
    return p;
  }
  function focusables(container) {
    var list = container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), summary');
    return Array.prototype.filter.call(list, function (el) { return el.offsetWidth > 0 || el.offsetHeight > 0; });
  }

  /* ------------------------------------------------------------ 每個 section 一份 */
  function init(root) {
    if (root._csP02) { return; }
    root._csP02 = true;
    root.classList.add('is-js');
    var sid = root.getAttribute('data-section-id');
    var ctrl = null;
    var moreCtrl = null;   // 「看更多」在途請求；apply()（篩選／排序換格）開始時會先 abort 它
    var timer = 0;
    var pvTimer = 0;
    var pvCtrl = null;
    var lastFocus = null;

    function piece(name) { return root.querySelector('[data-cs-swap="' + name + '"]'); }
    function drawer() { return piece('drawer'); }
    /* 第三十一輪：DESKTOP（>=990）原本的意思是「桌面有左側欄，所以抽屜被 css 收起（display:none）」。
       丙版面（root 有 cs-p02--c）桌面沒有側欄、抽屜在桌面也開得了，所以下面三處「>=990 就當抽屜不存在」的判斷改成「>=990 而且有側欄」才算。其他版面 hasSide() 恆為 true，行為不變。 */
    function hasSide() { return !root.classList.contains('cs-p02--c'); }

    var USE_RESET = root.getAttribute('data-cs-use-reset') === '1';
    function sectionUrl(params, path) {
      return (path || window.location.pathname) + '?' + params.toString() + '&section_id=' + encodeURIComponent(sid);
    }
    /* r40 F1：params 是空的就只回路徑，不帶尾巴的「?」（清除全部後網址不再多一個 ?） */
    function pageUrl(params, path) { var q = params.toString(); return (path || window.location.pathname) + (q ? '?' + q : ''); }
    /* r40 F1：最後一次換格完成的網址（路徑＋search）。popstate 時用它判斷「只有 hash 變」（例如抽屜的 #）：相同就不重抓 */
    var lastKey = window.location.pathname + window.location.search;

    function fetchDoc(params, signal, path, onSettle) {
      return fetch(sectionUrl(params, path), { credentials: 'same-origin', signal: signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { if (onSettle) { onSettle(); } if (!r.ok) { throw new Error('HTTP ' + r.status); } return r.text(); }, function (err) { if (onSettle) { onSettle(); } throw err; })
        .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
    }

    /* 收合狀態：swap 前記下、swap 後還原（伺服器判定「有已選值」的組仍強制展開） */
    function saveState() {
      var st = { det: {}, exp: {} };
      root.querySelectorAll('details[data-cs-key]').forEach(function (d) { st.det[d.getAttribute('data-cs-key')] = d.open; });
      root.querySelectorAll('ul[data-cs-key]').forEach(function (u) { st.exp[u.getAttribute('data-cs-key')] = u.classList.contains('is-expanded'); });
      return st;
    }
    function restoreState(st) {
      root.querySelectorAll('details[data-cs-key]').forEach(function (d) {
        var k = d.getAttribute('data-cs-key');
        if (k in st.det && !d.querySelector('.cs-p02-f__in:checked') && !d.querySelector('.cs-p02-price__num[value]:not([value=""])')) { d.open = st.det[k]; }
      });
      root.querySelectorAll('ul[data-cs-key]').forEach(function (u) {
        var k = u.getAttribute('data-cs-key');
        if (k in st.exp && st.exp[k]) {
          u.classList.add('is-expanded');
          var b = u.parentNode.querySelector('[data-cs-more]');
          if (b) { b.setAttribute('aria-expanded', 'true'); }
        }
      });
    }

    /* 第三十輪（審查補跑 Codex 第 21 輪 should-fix）：按「查看」套用 A、回應還沒回來時客人重開抽屜勾 B，
       A 回來 swap 會把整份表單換成伺服器印的（只有 A），B 不見了。
       做法：套用在途（root.is-loading）時，只記下客人在抽屜裡「實際動過」的項目（勾／取消、價錢數字、按了清除）；
       swap 時抽屜開著，就把這些改動重放到新表單。新表單裡找不到、或被伺服器印成 disabled 的項目不重放，算 skipped。
       不帶整份舊表單（第一版這樣做，Codex 指出會把停用的選項、額外排序、用途重設的組合帶錯）。
       記號 data-cs-p02-carry="重放:跳過"；關抽屜（＝放棄未套用改動）或開始新的套用就清掉紀錄。 */
    var inflightEdits = null;
    function noteEdit(i) {
      if (!i || !i.name || !root.classList.contains('is-loading')) { return; }
      var d = drawer();
      if (!d || !d.contains(i)) { return; }
      inflightEdits = inflightEdits || { inputs: {}, clear: false };
      // 先刪再寫：同一項被改第二次時排到最後，重放次序＝最後一次操作的次序（Codex r30b：排序 B→C→B 重放成 C）
      var put = function (k, r) { delete inflightEdits.inputs[k]; inflightEdits.inputs[k] = r; };
      if (i.type === 'radio' || (i.type === 'checkbox' && i.name === 'cs_use')) {
        // 互斥的一組（排序 radio、用途一次一個）：連同被自動取消的同組選項一起記當下狀態，被動的那個最後寫（Codex r30b：U→V→U 重放成 V）
        Array.prototype.forEach.call(d.querySelectorAll('input'), function (o) {
          if (o !== i && o.name === i.name && o.type === i.type) { put(o.name + '\u0001' + o.value, { t: 'c', g: 1, name: o.name, value: o.value, on: o.checked }); }
        });
        put(i.name + '\u0001' + i.value, { t: 'c', g: 1, name: i.name, value: i.value, on: i.checked });
      }
      else if (i.type === 'checkbox') { put(i.name + '\u0001' + i.value, { t: 'c', name: i.name, value: i.value, on: i.checked }); }
      else if (i.type === 'number' || i.type === 'text') { put(i.name + '\u0001' + (i.id || ''), { t: 'v', name: i.name, id: i.id || '', value: i.value }); }
    }
    function noteClear(d) {
      if (!root.classList.contains('is-loading')) { return; }
      inflightEdits = { inputs: {}, clear: true };   // 清除之前動過的都作廢，從清空開始記
      Array.prototype.forEach.call(d.querySelectorAll('.cs-p02-price__num'), noteEdit);
    }
    function replayEdits(d, form, ed) {
      var applied = 0, skipped = 0;
      if (ed.clear) {
        Array.prototype.forEach.call(form.querySelectorAll('.cs-p02-f__in:checked'), function (c) { c.checked = false; });
        Array.prototype.forEach.call(form.querySelectorAll('input[name="cs_tag"]'), function (h) { (d._csRemoved = d._csRemoved || []).push({ n: h, p: h.parentNode, s: h.nextSibling }); h.parentNode.removeChild(h); });
        Array.prototype.forEach.call(form.querySelectorAll('cs-p02-price'), function (p) { if (p.reset) { p.reset(); } });
        applied++;
      }
      var find = function (r) {
        return Array.prototype.filter.call(form.querySelectorAll('input'), function (i) {
          return i.name === r.name && (r.t === 'c' ? i.value === r.value : (i.id || '') === r.id);
        })[0];
      };
      // 互斥組裡被選的那個在新表單不存在或 disabled（例如額外排序隨排序消失）：整組都不動，保留伺服器已選的（Codex r30c）
      var badGroup = {};
      Object.keys(ed.inputs).forEach(function (k) {
        var r = ed.inputs[k], el;
        if (r.g && r.on) { el = find(r); if (!el || el.disabled) { badGroup[r.name] = true; } }
      });
      Object.keys(ed.inputs).forEach(function (k) {
        var r = ed.inputs[k];
        if (r.g && badGroup[r.name]) { skipped++; return; }
        var el = find(r);
        if (!el || el.disabled) { skipped++; return; }
        if (r.t === 'c') {
          if (r.on && r.name === 'cs_use') { Array.prototype.forEach.call(form.querySelectorAll('input[name="cs_use"]'), function (o) { if (o !== el) { o.checked = false; } }); }
          el.checked = r.on;
        } else { el.value = r.value; }
        applied++;
      });
      Array.prototype.forEach.call(form.querySelectorAll('cs-p02-price'), function (p) { if (p.fromNumbers) { p.fromNumbers(); } });
      return { applied: applied, skipped: skipped };
    }

    /* ---- 系列內搜尋（第三十一輪第二批 2A）----
       目的：客人不用離開系列頁就能在「已載入的卡」裡找商品。純前端過濾，不打伺服器；不呼叫 /search/suggest（繁中與日文會回 417）。
       兩個輸入框（側欄、抽屜）共用 qText 一份狀態，任一個輸入時另一個同步。輸入框沒有 name，所以 paramsFromForm 不會收到它，GET 表單也不會送出它。
       比對的字：卡片 DOM 裡 .card-information__text（商品標題連結／無圖時的 h3；看得見的標題）與 .card-article-info（品牌＝card-product 的 show_vendor 輸出）。
         選這兩個是因為它們是顧客在卡上實際讀到的標題與品牌；不用整張 li 的 textContent，那會把價錢、「加入購物車」、顏色名、快速預覽按鈕的字一起比對進去（搜「HK」會命中每一張價錢）。
         兩個都找不到（卡片結構變了）時退回整張 li 的文字，並在 root 寫 data-cs-p02-q-miss="退回的卡數"，不靜默退讓。
       多個詞（空白分隔）要全部命中（AND）；NFKC 正規化加 toLowerCase，所以不分大小寫、全形半形視為相同。
       隱藏：不符合的 li.cs-p02__item 設 hidden；有字時 grid 裡不是 .cs-p02__item 的 li（之後的宣傳框帶 data-cs-p02-promo）也一律隱藏。我們隱藏的 li 都加 data-cs-q-hid，清空時只還原有這個記號的，不動別人設的 hidden。
       換格（apply 換掉四塊）與看更多追加之後都重新套用，並把關鍵字寫回新的輸入框（側欄與抽屜整塊重建）。
       IME：組字中（isComposing）不過濾，等 compositionend，不然注音／拼音的中間字會讓整頁商品瞬間消失。 */
    var qText = '';
    function qNorm(s) { s = String(s === null || s === undefined ? '' : s); if (s.normalize) { s = s.normalize('NFKC'); } return s.toLowerCase(); }
    function qInputs() { return Array.prototype.slice.call(root.querySelectorAll('[data-cs-p02-q]')); }
    function qReveal() { Array.prototype.forEach.call(root.querySelectorAll('[data-cs-p02-qbox]'), function (b) { b.hidden = false; }); }
    function qHide(li, on) {
      // 已經被別人（例如去重）設成 hidden 的 li 不認領：不加我們的記號，清空時才不會被我們誤還原
      if (on) { if (!li.hidden) { li.hidden = true; li.setAttribute('data-cs-q-hid', ''); } }
      else if (li.hasAttribute('data-cs-q-hid')) {
        if (!li.hasAttribute('data-cs-promo-off')) { li.hidden = false; }   // 第三十一輪第三批：日期不在期間內的宣傳框（data-cs-promo-off）清空搜尋時不還原，只拿掉我們的搜尋記號
        li.removeAttribute('data-cs-q-hid');
      }
    }

    /* ---- 宣傳框日期後備（第三十一輪第三批戊）----
       伺服器依日期輸出或不輸出宣傳框（li[data-cs-p02-promo]，data-start／data-end 是 YYYYMMDD 或空），但 Shopify 頁面快取可能讓過期的框繼續被送出，所以 JS 再以香港時間（UTC+8）算一次今天：
       在期間內（含頭尾）→ 拿掉 hidden；不在 → 設 hidden 並加 data-cs-promo-off。
       和搜尋的分工：搜尋隱藏用 data-cs-q-hid、日期隱藏用 data-cs-promo-off，兩個記號互不認領。
       日期判斷在期間內時拿掉 hidden（包括伺服器因「開始日未到」輸出的、沒有任何記號的 hidden），緊接著 qApply 會依搜尋字再把它隱藏並加上 data-cs-q-hid；
       不在期間內時設 hidden＋data-cs-promo-off，搜尋的記號（若有）原樣留著。清空搜尋時 qHide 看到 data-cs-promo-off 只拿掉搜尋記號、不還原，所以過期的框不會因為清空搜尋而跑出來。
       時間用 Date.now()（測試時可覆寫）。載入時、每次 cs-p02:swap（換格與看更多）、bfcache 還原（pageshow persisted）後各做一次。 */
    function hkToday() {
      var d = new Date(Date.now() + 8 * 3600 * 1000);
      return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
    }
    /* 真實日期驗證（Codex 審查，與 snippets/cs-p02-promo.liquid 同一套規則）：YYYYMMDD（伺服器輸出的格式）或 YYYY-MM-DD；年 2000–2999、月 1–12、日 1 到當月天數（Date.UTC(y, m, 0) 取當月最後一天，閏年自動算對）。
       不合法（含空字串）一律回 0＝當作留空。原本 parseInt 會把 20261301、20260231 當成有效的數字參與比較。 */
    function ymd(s) {
      s = String(s === null || s === undefined ? '' : s).replace(/^\s+|\s+$/g, '');
      var m = /^(\d{4})(\d{2})(\d{2})$/.exec(s) || /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
      if (!m) { return 0; }
      var y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
      if (y < 2000 || y > 2999 || mo < 1 || mo > 12 || d < 1 || d > new Date(Date.UTC(y, mo, 0)).getUTCDate()) { return 0; }
      return y * 10000 + mo * 100 + d;
    }
    function promoSync() {
      var today = hkToday();
      Array.prototype.forEach.call(root.querySelectorAll('[data-cs-p02-promo]'), function (li) {
        var s = ymd(li.getAttribute('data-start'));
        var e = ymd(li.getAttribute('data-end'));
        if ((e > 0 && today > e) || (s > 0 && today < s)) {
          li.setAttribute('data-cs-promo-off', '');
          li.hidden = true;
        } else {
          li.removeAttribute('data-cs-promo-off');
          li.hidden = false;   // 搜尋正在隱藏它的話，呼叫端接著跑 qApply，會依搜尋字再隱藏（init、cs-p02:swap、pageshow 三處都是 promoSync 後接 qApply）
        }
      });
    }
    /* ---- 宣傳框閱讀順序（Codex 審查）----
       伺服器把宣傳框印在第 3 張卡之後（欄數是 CSS 才知道的事，伺服器不知道）。沒有 JS 時：3 欄（桌面／平板；沒有 JS 就沒有乙收合側欄，4 欄的門檻 container >= 1124px 到不了——
       側欄展開時主格最寬 948px，實測於 156-fixes2.mjs E6）自然排在第一列之後，視覺＝DOM＝Tab；2 欄手機靠 css 的 grid-row:3 畫在第 4 張之後（DOM 在第 3 張之後，差一張，已知殘留）。
       有 JS 時：依 ul 實際欄數（getComputedStyle(ul).gridTemplateColumns 數出來的軌道數）把 li 搬到第 N 張 .cs-p02__item 之後——欄數 >= 3 時 N＝欄數，2 欄時 N＝4——
       搬完加上 data-cs-promo-placed，css 才放掉 grid-row 的強制（沒有這個屬性時 >=750 用 grid-row:2、<750 用 grid-row:3，所以 JS 晚到或失敗時 2／3／4 欄的視覺位置也對），
       由自動排列把它放在那一列，所以視覺順序＝DOM 順序＝Tab 順序。
       冪等：已經在第 N 張之後就什麼都不做（不動 DOM）。觸發時機：載入、cs-p02:swap（換格與看更多）、pageshow（bfcache）、ul 尺寸改變（視窗寬度、乙收合側欄；ResizeObserver）。
       宣傳框被搜尋或日期隱藏時不用處理（它本來就不佔位）。丙（flex-grid）沒有宣傳框、ul 也不是 grid，欄數讀出 0 就直接略過。 */
    function promoGrid() { var g = piece('grid'); return g && g.querySelector(':scope > ul'); }
    function promoPlace() {
      var ul = promoGrid();
      var promo = ul && ul.querySelector(':scope > li[data-cs-p02-promo]');
      if (!promo) { return; }
      var tracks = window.getComputedStyle(ul).gridTemplateColumns;
      var cols = (!tracks || tracks === 'none') ? 0 : tracks.split(/\s+/).filter(function (x) { return x; }).length;
      var items = ul.querySelectorAll(':scope > li.cs-p02__item');
      if (cols < 1 || !items.length) { return; }
      var n = cols >= 3 ? cols : 4;
      var anchor = items[Math.min(n, items.length) - 1];
      if (anchor.nextElementSibling !== promo) { ul.insertBefore(promo, anchor.nextSibling); }
      // r31h：排好之後才加標記，css 才放掉 grid-row 的強制（見 css 宣傳框區塊）。換格、看更多重印的新 li 沒有這個屬性，下一次 promoPlace 會重新加上。
      if (!promo.hasAttribute('data-cs-promo-placed')) { promo.setAttribute('data-cs-promo-placed', ''); }
    }
    var promoRO = null;
    function promoWatch() {
      var ul = promoGrid();
      if (typeof window.ResizeObserver === 'undefined') { return; }   // 沒有 ResizeObserver 的舊瀏覽器：欄數改變時不重排（載入、換格、看更多時仍會排）
      if (!promoRO) { promoRO = new window.ResizeObserver(function () { promoPlace(); }); }
      promoRO.disconnect();
      if (ul) { promoRO.observe(ul); }
    }
    function qStatusInto(el, q, matched, loaded, total) {
      el.textContent = '';
      if (!q) { return; }
      var tm = root.getAttribute('data-t-qmatch') || '__X__ / __Y__';
      var text = tm.replace('__X__', String(matched)).replace('__Y__', String(loaded));
      var more = total - loaded;
      if (more > 0) { text += ' ' + (root.getAttribute('data-t-qleft') || '__N__').replace('__N__', String(more)); }
      el.appendChild(document.createTextNode(text));
      if (more > 0) {
        var base = root.getAttribute('data-cs-search-url') || '/search';
        var a = document.createElement('a');
        a.href = base + (base.indexOf('?') >= 0 ? '&' : '?') + 'q=' + encodeURIComponent(q) + '&type=product';
        a.textContent = (root.getAttribute('data-t-qsite') || '__Q__').replace('__Q__', q);
        el.appendChild(document.createTextNode(' '));
        el.appendChild(a);
      }
    }
    function qApply() {
      var grid = piece('grid');
      var q = qText.replace(/^\s+|\s+$/g, '');
      var tokens = qNorm(q).split(/\s+/).filter(function (x) { return x; });
      var items = grid ? grid.querySelectorAll(':scope > ul > li.cs-p02__item') : [];
      var others = grid ? grid.querySelectorAll(':scope > ul > li:not(.cs-p02__item)') : [];
      var matched = 0, miss = 0;
      Array.prototype.forEach.call(items, function (li) {
        var ok = true;
        if (tokens.length) {
          var parts = li.querySelectorAll('.card-information__text, .card-article-info');
          var text = '';
          Array.prototype.forEach.call(parts, function (n) { text += ' ' + n.textContent; });
          if (!text.replace(/\s+/g, '')) { miss++; text = li.textContent; }
          text = qNorm(text);
          ok = tokens.every(function (t) { return text.indexOf(t) >= 0; });
        }
        if (ok) { matched++; }
        qHide(li, !ok);
      });
      Array.prototype.forEach.call(others, function (li) { qHide(li, tokens.length > 0); });
      var loaded = items.length;
      var ce = root.querySelector('[data-cs-p02-count]');
      var total = ce ? parseInt(String(ce.textContent).replace(/[^0-9]/g, ''), 10) : NaN;
      if (!(total >= loaded)) { total = loaded; }   // 讀不到或比已載入還少（不可能）：當作全部都已載入，不印「還有」
      Array.prototype.forEach.call(root.querySelectorAll('[data-cs-p02-q-status]'), function (s) { qStatusInto(s, q, matched, loaded, total); });
      if (q) { root.setAttribute('data-cs-p02-q-state', matched + '/' + loaded); } else { root.removeAttribute('data-cs-p02-q-state'); }
      if (miss) { root.setAttribute('data-cs-p02-q-miss', String(miss)); } else { root.removeAttribute('data-cs-p02-q-miss'); }
    }
    function qSetText(v, from) {
      qText = v;
      qInputs().forEach(function (i) { if (i !== from && i.value !== v) { i.value = v; } });
      qApply();
    }
    function qFromInputs() {
      var v = '';
      qInputs().forEach(function (i) { if (!v && i.value) { v = i.value; } });
      qSetText(v, null);
    }
    /* 換格之後：新節點的輸入框是空的且帶 hidden → 寫回關鍵字、拿掉 hidden、重新套用；打字時被換掉的輸入框，焦點與游標接回新的 */
    function qRestore(focusId) {
      qInputs().forEach(function (i) { if (i.value !== qText) { i.value = qText; } });
      qReveal();
      qApply();
      if (focusId) {
        var nf = document.getElementById(focusId);
        if (nf && nf.matches && nf.matches('[data-cs-p02-q]')) {
          nf.focus();
          try { nf.setSelectionRange(nf.value.length, nf.value.length); } catch (err) { /* 有些輸入型別不支援，略 */ }
        }
      }
    }
    root.addEventListener('input', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('[data-cs-p02-q]') || e.isComposing) { return; }
      qSetText(t.value, t);
    });
    root.addEventListener('compositionend', function (e) {
      var t = e.target;
      if (t && t.matches && t.matches('[data-cs-p02-q]')) { qSetText(t.value, t); }
    });
    root.addEventListener('keydown', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('[data-cs-p02-q]') || e.key !== 'Enter' || e.isComposing || e.keyCode === 229) { return; }
      e.preventDefault();   // 抽屜裡的輸入框在 form 內，Enter 的預設是送出（＝換格）；側欄那個在 form 外，同樣不做事
      if (t.closest('.cs-p02__drawer')) { t.blur(); }   // 手機：收鍵盤，讓客人看到「符合 N 件」並關抽屜看結果
    });
    window.addEventListener('pageshow', function (ev) { if (ev.persisted) { qFromInputs(); } });   // 上一頁回來：瀏覽器可能還原了輸入框的字卻沒送 input 事件

    /* r40（m205 第四次驗證）：swap 移除有焦點的價錢框時，Chrome 會同步送 focusout；用 swapping 標記讓價錢框的 focusout 結段邏輯略過（那不是客人離開） */
    var swapping = false;
    function swap(doc) {
      swapping = true;
      root.setAttribute('data-cs-swapping', '1');   // 給 <cs-p02-price> 的 change（在 root 之外註冊）看
      try { return swapInner(doc); } finally { swapping = false; root.removeAttribute('data-cs-swapping'); }
    }
    function swapInner(doc) {
      var i, cur, nxt;
      for (i = 0; i < SWAP.length; i++) {
        if (!piece(SWAP[i]) || !doc.querySelector('[data-cs-swap="' + SWAP[i] + '"]')) { return false; }
      }
      var st = saveState();
      var qFocusId = (document.activeElement && document.activeElement.matches && document.activeElement.matches('[data-cs-p02-q]')) ? document.activeElement.id : '';   // 第三十一輪第二批：打字時被換掉的搜尋框，換完把焦點接回
      // 第二十一輪 D1：抽屜是 swap 的四塊之一，換掉之後舊節點上的 is-open 就沒了，body 上的鎖卻還在。
      // 選 (i)：抽屜在換之前是開著的（使用者在等回應時點了「篩選」），換完把開啟狀態接到新節點上，焦點原本在舊抽屜裡就移到新抽屜；
      // 然後一律交給 syncScrollLock 對帳，不在這裡直接加減 class。
      var oldDrawer = drawer();
      var wasOpen = !!(oldDrawer && oldDrawer.classList.contains('is-open'));
      var focusWasIn = !!(wasOpen && oldDrawer.contains(document.activeElement));
      // 第二十二輪：商品卡上的原生快速預覽（quick-view-drawer）在 grid 裡，grid 被換掉時開著的快速預覽跟著消失，body 上的 quick-view--* class 卻沒人還原。
      // 做法 (ii)：換之前先用原生公開的 closeMenuDrawer 把開著的關掉（它會一併拿掉 body 的 click 監聽，之後不會在別處點擊時又冒出 closing），
      // 換完用 watchQuickView 在有上限的時間內把「class 還在、頁面上沒有開著的快速預覽」的殘留收掉。
      var qvInvolved = quickViewClasses().length > 0;
      var oldQds = Array.prototype.slice.call(root.querySelectorAll('quick-view-drawer'));
      oldQds.forEach(function (qd) {
        var dt = qd.querySelector('details');
        if (dt && dt.hasAttribute('open')) {
          qvInvolved = true;
          if (typeof qd.closeMenuDrawer === 'function') { try { qd.closeMenuDrawer(new CustomEvent('cs-p02:swap')); } catch (e) { /* 原生關閉失敗：交給下面的對帳 */ } }
        }
        // 第三十輪（Codex 第 22 輪 should-fix）：這個實例馬上會被換掉。原生 openMenuDrawer 的 setTimeout 若還沒跑，之後會替它在 body 掛 click，
        // 下一次點頁面就多跑一次關閉動畫。原生是 `this.onBodyClickEvent = this.onBodyClickEvent || bind(...)`，所以先拿掉已掛的，
        // 再把它換成「第一次被叫到就把自己拿掉、什麼都不做」的函式；之後才掛也無害，不依賴監看的時間窗。
        if (qd.onBodyClickEvent) { document.body.removeEventListener('click', qd.onBodyClickEvent); }
        var once = function () { document.body.removeEventListener('click', once); };
        qd.onBodyClickEvent = once;
      });
      /* r40 Codex f：請求在途時客人又打了字（schedule 還排著）。回應的表單只有發出時的數字，直接換上去會蓋掉最新的字，
         下一個字又會取消排著的 debounce，中間打的就永遠丟了（打 1、在途時打 5、舊回應回來、再打 0 → 10，不是 150）。
         換之前記下桌機表單價錢框的現值，換完依 id 寫回新表單。排著的 debounce 仍讀舊表單（那是客人最新的整份狀態），不改。 */
      var carryPrice = null;
      var dfOld = timerPending ? root.querySelector('form[data-cs-p02-form="d"]') : null;
      if (dfOld) {
        carryPrice = {};
        Array.prototype.forEach.call(dfOld.querySelectorAll('.cs-p02-price__num'), function (n) { if (n.id) { carryPrice[n.id] = n.value; } });
      }
      for (i = 0; i < SWAP.length; i++) {
        cur = piece(SWAP[i]);
        nxt = doc.querySelector('[data-cs-swap="' + SWAP[i] + '"]');
        cur.replaceWith(document.importNode(nxt, true));
      }
      restoreState(st);
      if (carryPrice) {
        var dfNew = root.querySelector('form[data-cs-p02-form="d"]');
        if (dfNew) {
          Array.prototype.forEach.call(dfNew.querySelectorAll('.cs-p02-price__num'), function (n) { if (n.id && n.id in carryPrice && n.value !== carryPrice[n.id]) { n.value = carryPrice[n.id]; } });
          Array.prototype.forEach.call(dfNew.querySelectorAll('cs-p02-price'), function (p) { if (p.fromNumbers) { p.fromNumbers(); } });
        }
      }
      // Codex 審查：先把開啟狀態接到新抽屜，再 qRestore。抽屜關著時是 visibility:hidden，對裡面的搜尋框 focus() 會失敗（焦點掉到 body），
      // 後面 refocusDrawer 就會把焦點搬到抽屜第一個控制項——看起來像 refocusDrawer 蓋掉了 qRestore，其實是 qRestore 根本沒成功。
      if (wasOpen && drawer()) { drawer().classList.add('is-open'); }
      qRestore(qFocusId);   // 新的側欄／抽屜裡搜尋框是空的且帶 hidden：寫回關鍵字、顯示、對新的商品格重新套用
      var qKept = !!(qFocusId && document.activeElement && document.activeElement.id === qFocusId);   // 換格前焦點在搜尋框、而且已接回新的搜尋框：refocusDrawer 不覆寫
      if (wasOpen && inflightEdits && drawer()) {
        var nd = drawer(), nf = nd.querySelector('form');
        if (nf) {
          var rp = replayEdits(nd, nf, inflightEdits);
          root.setAttribute('data-cs-p02-carry', rp.applied + ':' + rp.skipped);
          if (rp.applied) { updateSel(nf); schedulePreview(0); }
        }
      }
      inflightEdits = null;
      syncScrollLock('swap');
      if (qvInvolved) { watchQuickView(); }
      if (focusWasIn && drawerShown() && !qKept) { refocusDrawer(); }
      // 通知同頁的 B3（cs-p02-continue.js）：商品格換了，要重新去重
      document.dispatchEvent(new CustomEvent('cs-p02:swap', { detail: { section: sid } }));
      return true;
    }

    function setBusy(on) {
      root.classList.toggle('is-loading', on);
      var g = piece('grid');
      if (g) { g.setAttribute('aria-busy', on ? 'true' : 'false'); }
    }

    /* r40 F1：fromPop＝這次是 popstate（上一頁／下一頁）觸發的。一般操作成功後 pushState（網址有變）或 replaceState（沒變）；
       popstate 來的成功後不動歷史紀錄，失敗與逾時的整頁導向改用 location.replace（不多出一筆歷史紀錄）。 */
    /* r40 F1 追加（架構師 10-20；Codex c 後改寫）：價錢框同一段打字只佔 1 筆歷史紀錄。
       「段」用 id 標記，id 寫在那一筆歷史紀錄的 state（csP02Seg）裡；只有「目前這一筆的 state 帶著同一個段 id」才 replaceState，
       所以不會蓋到別的 root、別的操作或上一頁回去的那一筆（不靠記憶體裡的布林值）。
       - histMode 'typing'（價錢框 input 的 debounce）：沒有進行中的段就開一個新段；目前這筆屬於本段→replace，否則網址有變→push（帶段 id）。
       - histMode 'price-end'（價錢框 change：失焦，或在價錢框按 Enter 的 submit）：發出時就結束本段；目前這筆屬於本段→replace 收尾，否則照常 push。
       - 其他（勾選、排序、滑桿放手、chip、清除全部；空白）：發出時就結束本段（不等成功，被下一段打字取消也算結束），網址有變就 push。
       - popstate：立刻結束本段。
       - 失敗／逾時的整頁導向：fromPop，或目前這筆屬於本段（成功的話本來會 replace）→ location.replace；否則 location.href。 */
    var typingSeg = '';
    var segSeq = 0;
    function apply(params, refocusId, focusOpen, path, fromPop, histMode) {
      var seg = '';
      if (!fromPop) {
        if (histMode === 'typing') { if (!typingSeg) { segSeq += 1; typingSeg = sid + ':' + Date.now() + ':' + segSeq; } seg = typingSeg; }
        else if (histMode === 'price-end') { seg = typingSeg; typingSeg = ''; }
        else { typingSeg = ''; }
      }
      /* Codex d：所有權在發出時拍快照（目前這筆的 state 帶本段 id），完成或失敗時再確認網址沒換過（還是同一筆）。
         別的腳本中途清掉 state 也照樣 replace；別的腳本中途 push 了新網址（即使複製了 state）就不算本段，改 push／href。 */
      var startKey = window.location.pathname + window.location.search;
      var stAtStart = window.history.state;
      var ownedAtStart = !!(seg && stAtStart && stAtStart.csP02Seg === seg);
      var ownsEntry = function () { return ownedAtStart && window.location.pathname + window.location.search === startKey; };
      var fallback = function () { if (fromPop || ownsEntry()) { window.location.replace(pageUrl(params, path)); } else { window.location.href = pageUrl(params, path); } };
      inflightEdits = null;
      if (moreCtrl) { moreCtrl.abort(); moreCtrl = null; }   // 看更多在途時又換格：舊請求作廢（回來也會再檢查 grid 有沒有被換過，兩道）
      if (ctrl) { ctrl.abort(); }
      ctrl = new AbortController();
      var mine = ctrl;
      setBusy(true);
      /* S-B7 保險（Codex 第 16 條改版）：只在「fetch 完全沒有回應」（既沒 resolve 也沒 reject）超過 10 秒時，才直接導向組好的網址。
         fetch 回了非 2xx、或 reject（斷網、被擋）→ 下面的 catch 立刻導向，不等這個計時器；
         回應有到、只是 swap 還在做 → 計時器已經被 onSettle 清掉，不會中途整頁重載。
         為什麼是 10 秒：之前的 5 秒沒有實測依據，慢網（例如行動網路）的正常 Section Rendering 也可能超過 5 秒，會打斷捲動與焦點；
         10 秒對應「請求已經卡死、使用者早就在等」的程度（一般瀏覽器對這種請求沒有這麼快放棄，所以才需要我們出手）。
         🟡 10 秒這個數字也不是量出來的：店面沒有 Section Rendering 耗時的量測；推後要用 Network 節流（Slow 3G）量 p95 再校正。 */
      var wd = window.setTimeout(function () {
        if (ctrl === mine) { fallback(); }
      }, 10000);
      return fetchDoc(params, mine.signal, path, function () { window.clearTimeout(wd); }).then(function (doc) {
        /* Codex 複審(b)：請求在途時使用者可能把焦點移走（例如改去打搜尋框）。swap 會把舊節點換掉，之後依 refocusId 把焦點「還給」原控制項就會搶走使用者現在的焦點。
           換之前先記下：焦點是否還在原控制項所屬的區塊（同一個 [data-cs-swap] 片段；沒有片段就只認控制項本身）而且不在搜尋框。不是的話就不還。
           換之後再看一次：焦點如果已經是搜尋框（swap 裡 qRestore 接回去的），也不動它。 */
        if (ctrl !== mine || mine.signal.aborted) { return; }   // r40 Codex b：已被作廢的請求（例如 popstate 回到 lastKey）回來了也不換格
        var rfEl = refocusId ? document.getElementById(refocusId) : null;
        var rfBlock = rfEl ? (rfEl.closest('[data-cs-swap]') || rfEl) : null;
        var ae0 = document.activeElement;
        var holdRefocus = !!(rfBlock && (!ae0 || ae0 === document.body || (rfBlock.contains(ae0) && !(ae0.matches && ae0.matches('[data-cs-p02-q]')))));
        var rfId = (holdRefocus && ae0 && ae0 !== document.body && ae0 !== rfEl && ae0.id && rfBlock.contains(ae0)) ? ae0.id : refocusId;   // r40：焦點已移到同區塊另一個控制項（例如 Tab 到上限框）就還給它，不搶回發出請求的那個   // 焦點在 body／null（例如 Safari 點 checkbox 不會聚焦）維持原行為：照樣還給控制項
        if (!swap(doc)) { throw new Error('missing swap piece'); }
        if (!fromPop) {
          var nuUrl = new URL(pageUrl(params, path), window.location.href);
          var changed = nuUrl.pathname + nuUrl.search !== window.location.pathname + window.location.search;
          if (ownsEntry()) { window.history.replaceState({ csP02: 1, csP02Seg: seg }, '', pageUrl(params, path)); }   // 同一段價錢打字：蓋掉同一筆
          else if (changed) { window.history.pushState({ csP02: 1, csP02Seg: histMode === 'typing' ? seg : '' }, '', pageUrl(params, path)); }
          else { window.history.replaceState(window.history.state, '', pageUrl(params, path)); }
        }
        lastKey = window.location.pathname + window.location.search;
        setBusy(false);
        if (focusOpen && !drawerShown()) {
          var ob = root.querySelector('[data-cs-p02-open]');
          if (ob) { ob.focus(); }
        } else if (holdRefocus) {
          var ae1 = document.activeElement;
          var el = document.getElementById(rfId);
          if (el && !(ae1 && ae1.matches && ae1.matches('[data-cs-p02-q]'))) {
            el.focus();
            // r40（架構師 10-20 裁定甲）：換格後還焦點給新的價錢框（type=number）時游標在框首，慢打「1、停、5」會變成 51。只在這裡（我們自己還焦點）把值重設一次讓游標到尾端；客人自己點進框不經過這裡
            if (document.activeElement === el && el.matches('.cs-p02-price__num') && el.value !== '') { var cv = el.value; el.value = ''; el.value = cv; }
          }
        }
      }).catch(function (err) {
        window.clearTimeout(wd);
        if (err && err.name === 'AbortError') { syncScrollLock('abort'); return; }
        if (ctrl !== mine) { syncScrollLock('abort'); return; }   // r40 Codex b：已被作廢的請求失敗了也不導向（不可以把客人帶回舊網址）
        syncScrollLock('fail');
        fallback();
      });
    }

    /* ---- 看更多（第三十一輪丙）----
       點 [data-cs-p02-more]（真連結，沒有 JS 時整頁到 ?page=N）時攔下，用 section rendering 抓下一頁、只取新 grid 裡的 li.cs-p02__item、
       以 data-handle 去重（只比 .cs-p02__item[data-handle]，不掃整個 main）後接在目前的 ul 後面。不改網址（不 pushState page），不做無限捲動。
       請求網址：目前網址的參數（query）＋page=N（N 取自按鈕 href 的 page）＋view=csView()，路徑用 location.pathname；不拿 paginate.next.url 直接 fetch（語言路由會改寫）。
       競態：moreCtrl 會被 apply()（篩選／排序換格）abort；回來時也再檢查 grid 還是不是發請求時那一個、按鈕還在不在，不是就整批丟棄。
       失敗（非 2xx、斷網、逾時 MORE_TIMEOUT）：不整頁導覽；按鈕放回可按，role=status 印失敗字。
       成功後送 cs-p02:swap，讓 cs-p02-continue.js 重新去重（該檔是唯一的聽者，重跑冪等：見 cs-p02-continue.js 的 apply／seedSeen）。 */
    var MORE_TIMEOUT = 20000;
    function setStatus(bar, msg) { var s = bar && bar.querySelector('[data-cs-p02-status]'); if (s) { s.textContent = msg; } }
    function setMoreBusy(btn, on) {
      if (on) { btn.setAttribute('aria-busy', 'true'); btn.setAttribute('aria-disabled', 'true'); }
      else { btn.removeAttribute('aria-busy'); btn.removeAttribute('aria-disabled'); }
    }
    function nextPageOf(btn) {
      try { return parseInt(new URL(btn.getAttribute('href'), window.location.href).searchParams.get('page'), 10) || 0; } catch (e) { return 0; }
    }
    function loadMore(btn, page) {
      var grid = piece('grid');
      var bar = btn.closest('[data-cs-p02-morebar]');
      if (!grid || !bar || btn.getAttribute('aria-busy') === 'true' || root.classList.contains('is-loading')) { return; }
      var params = new URLSearchParams(window.location.search);
      params.set('page', String(page));
      setView(params);
      if (moreCtrl) { moreCtrl.abort(); }
      moreCtrl = new AbortController();
      var mine = moreCtrl;
      var timedOut = false;
      var to = window.setTimeout(function () { timedOut = true; mine.abort(); }, MORE_TIMEOUT);
      setMoreBusy(btn, true);
      setStatus(bar, '');
      fetchDoc(params, mine.signal).then(function (doc) {
        window.clearTimeout(to);
        if (moreCtrl !== mine || piece('grid') !== grid || !btn.isConnected) { return; }   // 換格過了（或被取代）：丟棄
        var gridNew = doc.querySelector('[data-cs-swap="grid"]');
        var ul = grid.querySelector(':scope > ul');
        if (!gridNew || !ul) { throw new Error('missing grid'); }
        // Codex 審查：用 Set，不用 {}——物件字面量有原型，handle 叫 constructor／toString／__proto__ 的商品會被當成「已經有了」而被丟掉
        var have = new Set();
        Array.prototype.forEach.call(grid.querySelectorAll('.cs-p02__item[data-handle]'), function (li) { have.add(li.getAttribute('data-handle')); });
        var added = [];
        Array.prototype.forEach.call(gridNew.querySelectorAll(':scope > ul > li.cs-p02__item'), function (li) {
          var h = li.getAttribute('data-handle');
          if (h) { if (have.has(h)) { return; } have.add(h); }
          var node = document.importNode(li, true);
          ul.appendChild(node);
          added.push(node);
        });
        /* Codex 複審(c)：等回應的時候使用者可能已經把焦點移走（例如回到搜尋框打字）。只有焦點還停在看更多按鈕上、或在 body／null（按鈕被 disabled／移除後瀏覽器丟掉的焦點）時，
           才把焦點移到新卡／按鈕／status；焦點已經在別處就不動。要在下面移除按鈕「之前」判斷（移除後 activeElement 會變成 body，分不出原本在哪）。 */
        var ae = document.activeElement;
        var moveFocus = !ae || ae === document.body || ae === document.documentElement || ae === btn;
        var nb = gridNew.querySelector('[data-cs-p02-more]');
        if (nb) {
          btn.setAttribute('href', nb.getAttribute('href'));
          var on = btn.querySelector('[data-cs-p02-more-n]');
          var nn = nb.querySelector('[data-cs-p02-more-n]');
          if (on && nn) { on.textContent = nn.textContent; }
          setMoreBusy(btn, false);
        } else if (btn.parentNode) {
          btn.parentNode.removeChild(btn);
        }
        moreCtrl = null;
        setStatus(bar, (bar.getAttribute('data-t-done') || '__N__').replace('__N__', String(added.length)));
        qApply();   // 第三十一輪第二批：追加的卡也要過濾（搜尋字還在的話）
        // Codex 審查：先過濾、再選焦點。原本先 focus 新批第一張再過濾，第一張不符合搜尋字時焦點落進隱藏的卡（焦點消失、讀屏跳到頁首）。
        // 現在：焦點給新批裡第一張「看得見」的卡（沒被搜尋隱藏）；新批全部被隱藏時，焦點留在看更多按鈕上（還在的話）；按鈕已經移除（最後一頁）就移到 status。
        var firstVis = null;
        added.forEach(function (n) { if (!firstVis && !n.hidden) { firstVis = n; } });
        var fl = firstVis && (firstVis.querySelector('a[href*="/products/"]') || firstVis.querySelector('a[href], button'));
        var stat = bar.querySelector('[data-cs-p02-status]');
        if (!moveFocus) { /* 使用者已經把焦點放在別處：不動 */ }
        else if (fl) { fl.focus(); }
        else if (btn.isConnected) { btn.focus(); }
        else if (stat) { stat.setAttribute('tabindex', '-1'); stat.focus(); }
        document.dispatchEvent(new CustomEvent('cs-p02:swap', { detail: { section: sid, reason: 'more', added: added.length } }));
      }).catch(function (err) {
        window.clearTimeout(to);
        if (moreCtrl !== mine) { return; }   // 被 apply() abort 或被新的看更多取代：安靜
        if (err && err.name === 'AbortError' && !timedOut) { return; }
        if (piece('grid') !== grid || !btn.isConnected) { return; }
        moreCtrl = null;
        setMoreBusy(btn, false);
        setStatus(bar, bar.getAttribute('data-t-fail') || '');
      });
    }

    /* S-B7：抽屜按「查看 N 件」。
       做法：按下當下才從表單讀「最終」條件（不用預覽當時的條件），先把還在排隊的預覽計時器與進行中的預覽請求全部取消，
       再用 apply() 抓 section 換畫面；apply() 有保險：fetch 失敗（reject 或非 2xx）或 swap 失敗立刻整頁導向同一個網址，fetch 10 秒完全沒回應也導向。 */
    var lastDrawerApply = 0;
    function applyFromDrawer(form) {
      lastDrawerApply = Date.now();
      window.clearTimeout(timer); timerPending = false; pendingMode = '';
      window.clearTimeout(pvTimer);
      if (pvCtrl) { pvCtrl.abort(); pvCtrl = null; }
      var r;
      try {
        r = resolveForm(form, false, USE_RESET);
      } catch (err) {
        window.location.href = window.location.pathname + (csView() ? '?view=' + csView() : '');
        return;
      }
      closeDrawer(true, undefined, true);   // 第三個參數：這是「套用」，不還原抽屜（還原會讓關門動畫中的勾一閃而過）
      apply(r.params, null, true, r.path);
    }

    /* C4：各組標題旁的「已選 N」跟著勾選即時更新（server 只在套用後才重印） */
    function updateSel(form) {
      var tpl = form.getAttribute('data-sel-tpl');
      if (!tpl) { return; }
      form.querySelectorAll('details.cs-p02-f').forEach(function (d) {
        var n = d.querySelectorAll('.cs-p02-f__in:checked').length;
        d.querySelectorAll('.cs-p02-price__num').forEach(function (i) { if (i.value !== '') { n = 1; } });
        var lab = d.querySelector('.cs-p02-f__sel');
        if (n > 0) {
          if (!lab) {
            lab = document.createElement('span');
            lab.className = 'cs-p02-f__sel';
            var pm = d.querySelector('.cs-p02-f__pm');
            pm.parentNode.insertBefore(lab, pm);
          }
          lab.textContent = tpl.replace('__N__', String(n));
        } else if (lab) {
          lab.parentNode.removeChild(lab);
        }
      });
    }

    /* r40 F1 追加：histMode 照傳給 apply（'typing'＝價錢框打字中的 debounce；'price-end'＝價錢框失焦／Enter；其他呼叫不帶） */
    var timerPending = false;   // Codex d：schedule 排了還沒跑（價錢框 focusout 用它判斷要不要立刻結束打字段）
    var pendingMode = '';       // Codex e：排著的是哪一種（'typing'／'price-end'／空白）
    // r41（Codex r41-c 1）：直接套用（chip、連結、上一頁、送出）前先取消排著的套用；不取消的話，計時器之後拿換格前的舊表單（T08）會把剛移除的篩選加回去
    function cancelSchedule() { window.clearTimeout(timer); timerPending = false; pendingMode = ''; }
    function schedule(form, delay, refocusId, useChanged, histMode) {
      window.clearTimeout(timer);
      timerPending = true;
      pendingMode = histMode || '';
      timer = window.setTimeout(function () {
        timerPending = false;
        pendingMode = '';
        // r40 Codex g：排著時換過格，舊表單已脫離 DOM；排序 select 在表單外以 form= 關聯，已改屬新表單，讀舊表單會漏送 sort_by。
        // Codex h：整份改讀新表單會丟掉在途時剛勾的篩選（新表單是伺服器照上一個請求印的）。表單內的值仍讀舊表單（客人最新的狀態），只把現行同種表單「表單外關聯」的控制項補上
        var ext = null;
        if (!form.isConnected) {
          var live = root.querySelector('form[data-cs-p02-form="' + form.getAttribute('data-cs-p02-form') + '"]');
          if (live) { ext = Array.prototype.filter.call(live.elements, function (el) { return !live.contains(el); }); }
        }
        var r = resolveForm(form, !!useChanged, USE_RESET, ext);
        apply(r.params, refocusId, false, r.path, false, histMode || '');
      }, delay);
    }

    /* ---- 抽屜：只更新底鍵的 N ---- */
    function previewCount() {
      var d = drawer();
      if (!d) { return; }
      var form = d.querySelector('form');
      var btn = d.querySelector('[data-cs-p02-apply]');
      if (!form || !btn) { return; }
      if (pvCtrl) { pvCtrl.abort(); }
      pvCtrl = new AbortController();
      var mine = pvCtrl;
      var pr = resolveForm(form, false, USE_RESET);
      var startedAt = Date.now();
      fetchDoc(pr.params, mine.signal, pr.path).then(function (doc) {
        var c = doc.querySelector('[data-cs-p02-count]');
        if (!c || lastDrawerApply > startedAt) { return; }
        if (btn._csAppliedText === undefined) { btn._csAppliedText = btn.textContent; }   // 第二十五輪：伺服器印的（已套用）字，只有預覽會改它，所以第一次改之前記下
        btn.textContent = (btn.getAttribute('data-tpl') || '__N__').replace('__N__', c.textContent.trim());
      }).catch(function () { /* 預覽失敗就維持舊數字，按下後仍照表單套用 */ });
    }
    function schedulePreview(delay) {
      window.clearTimeout(pvTimer);
      pvTimer = window.setTimeout(previewCount, delay);
    }

    /* 手機抽屜開啟時，第三方浮層（客服泡泡、WhatsApp 圓鈕）會蓋住抽屜右下角。
       第六輪處理：抽屜開著時 body 加 cs-p02-drawer-open，CSS 只在這個 class 存在時隱藏被標記的浮層，關閉就還原。
       🔴 浮層選擇器 MISS：存下來的系列頁 HTML（.v8-work/p02/smoke3、postpush3）只有 app 的 <script src>（dondy-whatsapp-chat-widget 的 ChatBubble.js、Shopify 聊天），
       浮層 DOM 是執行時才注入，HTML 裡沒有任何 id／class 可引用，所以不猜選擇器；改成開抽屜時量測：
       取右下角一塊區域的多個點，document.elementsFromPoint 找出不在抽屜裡、自己或祖先是 position:fixed 的元素，標 data-cs-p02-overlay。
       選擇「隱藏」而不是把抽屜 z-index 拉高：聊天小工具的 z-index 常是 2147483647，抽屜拉不過，而且浮層可能是 iframe。 */
    function markOverlays(d, isRetry) {
      var docEl = document.documentElement;
      var w = window.innerWidth || docEl.clientWidth;
      var h = window.innerHeight || docEl.clientHeight;
      var found = [];
      [24, 56, 96, 136].forEach(function (dx) {
        [24, 56, 96, 136, 176].forEach(function (dy) {
          var list = document.elementsFromPoint ? document.elementsFromPoint(w - dx, h - dy) : [];
          list.forEach(function (el) {
            if (d.contains(el) || el === document.body || el === docEl) { return; }
            var n = el;
            var fixed = null;
            while (n && n !== document.body) {
              if (window.getComputedStyle(n).position === 'fixed') { fixed = n; }
              n = n.parentElement;
            }
            if (fixed && !fixed.contains(d) && found.indexOf(fixed) === -1) { found.push(fixed); }
          });
        });
      });
      // S-C7：第二次量測（800ms 後）只有「找到東西」才覆寫記號；找不到就什麼都不動、也不印 MISS（第一次已經找到的情況不該被洗成 0）
      if (isRetry && !found.length) { return; }
      found.forEach(function (el) { el.setAttribute('data-cs-p02-overlay', ''); });
      // Codex 第 20 條：記號數字以 DOM 裡實際帶 data-cs-p02-overlay 的元素總數為準（首輪標 2 個、重試又新增 1 個時是 3，不是 1）
      var markedTotal = document.querySelectorAll('[data-cs-p02-overlay]').length;
      document.body.setAttribute('data-cs-p02-overlay-n', String(markedTotal));
      if (!markedTotal && window.console) { console.warn('cs-p02 drawer: MISS no fixed overlay found at bottom-right (nothing to hide)'); }
    }
    function clearOverlays() {
      Array.prototype.slice.call(document.querySelectorAll('[data-cs-p02-overlay]')).forEach(function (el) { el.removeAttribute('data-cs-p02-overlay'); });
      document.body.removeAttribute('data-cs-p02-overlay-n');
    }

    /* ---- 捲動鎖：單一出口（第二十一輪 D1）----
       鎖 = body 的 class cs-p02-drawer-open ＋ inline overflow:hidden（＋ 被標記的第三方浮層隱藏、「篩選」鍵的 aria-expanded）。
       鎖只由「抽屜此刻是否真的開著且看得見」決定：drawerShown() 為真才鎖、為假一律解。
       所有路徑結束後都呼叫 syncScrollLock(原因)：開、關、swap 完成、請求被取消、請求失敗、popstate、桌面寬度切換。不在別處各自加減 class 或 overflow。
       抽屜節點會被 swap 換掉，所以不能用「手上那個 d」判斷，每次都現找 drawer()。
       解鎖時 inline overflow 還原成「上鎖之前」的值（lockPrev），而且只在它還是我們設的 hidden 時才還原，不蓋掉別人後來改的值。
       自我解除的記號（A-6）：不是正常關閉（原因不是 close）卻發現「鎖著而抽屜沒開」時，解鎖並在 root 寫 data-cs-p02-lock-recovered="原因:次數"（只更新這一個屬性，不洗版、不用 console）。 */
    var lockPrev = null;
    var recoverCount = {};
    function drawerShown() {
      var d = drawer();
      return !!(d && d.isConnected && d.classList.contains('is-open') && !(DESKTOP.matches && hasSide()) && !d.hidden && d.getClientRects().length > 0);
    }
    function syncScrollLock(reason) {
      var body = document.body;
      var want = drawerShown();
      var hasClass = body.classList.contains('cs-p02-drawer-open');
      var held = lockPrev !== null;
      if (want) {
        if (!hasClass) { body.classList.add('cs-p02-drawer-open'); }
        if (!held) { lockPrev = body.style.overflow; }
        body.style.overflow = 'hidden';
      } else if (hasClass || held) {
        body.classList.remove('cs-p02-drawer-open');
        if (held) {
          if (body.style.overflow === 'hidden') { body.style.overflow = lockPrev; }
          lockPrev = null;
        }
        clearOverlays();
        if (reason !== 'close') {
          recoverCount[reason] = (recoverCount[reason] || 0) + 1;
          root.setAttribute('data-cs-p02-lock-recovered', reason + ':' + recoverCount[reason]);
        }
      }
      // 「篩選」鍵會被 swap 換成伺服器印的新節點（aria-expanded 一律 false），這裡每次依實際狀態重設
      root.querySelectorAll('[data-cs-p02-open]').forEach(function (b) { b.setAttribute('aria-expanded', want ? 'true' : 'false'); });
    }
    /* ---- 快速預覽殘留（第二十二輪）----
       原生快速預覽（global.js MenuDrawer＋quick-view.js）開著時 body 有 quick-view--opening／open／closing，css 帶 overflow:hidden 並顯示遮罩。
       這些 class 是原生管的，我們不改它的管理邏輯，只在 swap 之後收「殘留」：body 有這些 class，而頁面上沒有任何開著的快速預覽（quick-view-drawer 裡沒有 details[open]）。
       時序：原生 opening 動畫的 rAF 最長 400ms 才補上 --open（可能在 swap 之後），closing 動畫也是 400ms，所以 swap 後只監看一小段時間：
       每 60ms 檢查一次、最多 12 次（約 700ms），而且「連續兩次檢查都是殘留」才收（避開使用者剛好又點開新快速預覽、details 還沒設 open 的那幾毫秒）；
       時間到就停，沒有常駐輪詢。新的 swap 會讓舊的監看作廢（qvWatchGen）。 */
    var qvWatchGen = 0;
    function quickViewClasses() {
      return Array.prototype.filter.call(document.body.classList, function (c) { return c.indexOf('quick-view--') === 0; });
    }
    function quickViewShown() { return !!document.querySelector('quick-view-drawer details[open]'); }
    /* 第三十輪（審查補跑 Codex 第 22 輪）：
       ① blocker：分頁在背景時 rAF 暫停，原生 opening 動畫要等回到前景才補上 --open，原本 700ms 的監看早就跑完 → 畫面變暗、不能捲動。
          改成分頁在背景時不計次、不輪詢，等 visibilitychange 回到前景再接著監看（同樣只監看約 700ms）。
       ② should-fix：原生 openMenuDrawer 用 setTimeout 才掛 body click；swap 先關掉、移除了舊預覽，那個 setTimeout 之後仍替脫離文件的舊實例掛上監聽，
          下一次點頁面會多跑一次關閉動畫。改在 swap 換掉舊實例前處理（見 swap 裡 once 那段），不靠這裡的監看時間窗。 */
    var qvVisHandler = null;   // 同一時間只留一個「等回前景」的監聽；新的監看開始就拿掉舊的（Codex 第三十輪：gen 換了舊監聽會留著）
    function watchQuickView() {
      var gen = ++qvWatchGen;
      var n = 0;
      var prevStray = false;
      if (qvVisHandler) { document.removeEventListener('visibilitychange', qvVisHandler); qvVisHandler = null; }
      (function tick() {
        if (gen !== qvWatchGen) { return; }
        if (document.hidden) {
          qvVisHandler = function () {
            if (document.hidden) { return; }
            document.removeEventListener('visibilitychange', qvVisHandler); qvVisHandler = null;
            tick();
          };
          document.addEventListener('visibilitychange', qvVisHandler);
          return;
        }
        // quick-view--closing 是原生關閉動畫進行中的記號（原生 closeAnimation 400ms 後自己拿掉，舊實例脫離文件也照樣拿），不算殘留；
        // 殘留指 --open／--opening（原生 openAnimation 的 400ms rAF 在關閉之後仍會補上 --open，就是這個）。
        var cls = quickViewClasses().filter(function (c) { return c !== 'quick-view--closing'; });
        var stray = cls.length > 0 && !quickViewShown();
        if (stray && prevStray) {
          cls.forEach(function (c) { document.body.classList.remove(c); });
          recoverCount['quick-view'] = (recoverCount['quick-view'] || 0) + 1;
          root.setAttribute('data-cs-p02-lock-recovered', 'quick-view:' + recoverCount['quick-view']);
          stray = false;
        }
        prevStray = stray;
        n++;
        if (n < 12) { window.setTimeout(tick, 60); }
      })();
    }
    function refocusDrawer() {
      window.requestAnimationFrame(function () {
        var d = drawer();
        if (!d || !drawerShown()) { return; }
        var f = focusables(d);
        (f[0] || d).focus();
      });
    }

    function openDrawer(trigger) {
      var d = drawer();
      if (!d) { return; }
      lastFocus = trigger || document.activeElement;
      if (!d.classList.contains('is-open')) { revertDrawer(d, 'open'); }   // 第二十五輪：開之前先確認是已套用狀態（涵蓋瀏覽器還原表單狀態等沒走關閉路徑的髒狀態）
      d.classList.add('is-open');
      syncScrollLock('open');
      if (!drawerShown()) { return; }
      markOverlays(d);
      // 客服小工具可能晚幾百毫秒才掛上，再量一次（抽屜可能已被 swap 換掉，所以現找、並確認還開著）
      window.setTimeout(function () { var cd = drawer(); if (cd && drawerShown()) { markOverlays(cd, true); } }, 800);
      refocusDrawer();
    }
    /* 第二十五輪：抽屜裡沒按「查看」的改動，關抽屜時丟掉，回到伺服器渲染的已套用狀態。
       已套用狀態的來源：input 的 defaultChecked／defaultValue（＝伺服器印的 checked／value 屬性；swap 用 importNode 換進來的節點屬性原樣保留，
       使用者改的是 checked／value 屬性之外的「髒」狀態，所以預設值不會被汙染；瀏覽器還原表單狀態也只動髒狀態）。
       預覽請求（previewCount）只讀回應裡的件數，不換抽屜 DOM，所以也不會把未套用的值渲染成預設值。
       還原：checkbox／radio（含排序）、價錢輸入（並叫 cs-p02-price 重畫滑桿）、被「清除」拿掉的 hidden cs_tag、各組已選小標、「查看 N 件」鍵的字；
       並取消還在排隊或在途的預覽，不然回應晚到會把未套用的件數寫回鍵上。
       套用在途（root.is-loading）時不還原：swap 會整個換掉抽屜，這時還原只會讓畫面先閃回舊狀態。 */
    function revertDrawer(d, why) {
      d = d || drawer();
      if (!d || root.classList.contains('is-loading')) { return; }
      window.clearTimeout(pvTimer);
      if (pvCtrl) { pvCtrl.abort(); pvCtrl = null; }
      var form = d.querySelector('form');
      if (!form) { return; }
      var n = 0;
      Array.prototype.forEach.call(form.querySelectorAll('input'), function (i) {
        if (i.hasAttribute('data-cs-p02-q')) { return; }   // 第三十一輪第二批：搜尋框不是篩選條件，關抽屜不可清掉搜尋字（它的 defaultValue 是空的，不跳過就會被還原成空）
        if (i.type === 'checkbox' || i.type === 'radio') { if (i.checked !== i.defaultChecked) { i.checked = i.defaultChecked; n++; } }
        else if (i.type === 'number' || i.type === 'text' || i.type === 'range') { if (i.value !== i.defaultValue) { i.value = i.defaultValue; n++; } }
      });
      if (d._csRemoved && d._csRemoved.length) {
        d._csRemoved.slice().reverse().forEach(function (r) {
          if (r.p && r.p.isConnected && !r.n.isConnected) { r.p.insertBefore(r.n, r.s && r.s.parentNode === r.p ? r.s : null); n++; }
        });
        d._csRemoved = [];
      }
      Array.prototype.forEach.call(form.querySelectorAll('cs-p02-price'), function (p) { if (p.fromNumbers) { p.fromNumbers(); } });
      var btn = d.querySelector('[data-cs-p02-apply]');
      if (btn && btn._csAppliedText !== undefined) { if (btn.textContent !== btn._csAppliedText) { btn.textContent = btn._csAppliedText; n++; } btn._csAppliedText = undefined; }
      updateSel(form);
      if (n) { root.setAttribute('data-cs-p02-drawer-revert', (why || 'close') + ':' + n); }   // 只在真的還原了東西時寫，印的是還原了幾項
    }

    /* why：抽屜其實沒開著的時候，呼叫者的原因（resize 等），讓 syncScrollLock 把殘留的鎖記成「自我解除」；applying：這次關閉是「套用」，不還原 */
    function closeDrawer(skipFocus, why, applying) {
      var d = drawer();
      if (!d || !d.classList.contains('is-open')) { syncScrollLock(why || 'close-idle'); return; }
      d.classList.remove('is-open');
      if (!applying) { inflightEdits = null; }   // 第三十輪：關抽屜＝放棄未套用的改動，在途期間記下的也不重放
      // 第二十五輪：關門動畫（.28s）跑完再還原，不讓客人看到勾在滑出時一個個消失；再開時 openDrawer 還會再還原一次（保險）
      if (!applying) { window.setTimeout(function () { var cd = drawer(); if (cd && !cd.classList.contains('is-open')) { revertDrawer(cd, why || 'close'); } }, 320); }
      // 網址帶著抽屜錨點進來時（:target 開啟），關閉要把錨點拿掉，否則 CSS 的 :target 會讓抽屜一直開著
      if (window.location.hash && window.location.hash === '#' + d.id && window.history && window.history.replaceState) {
        window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
      }
      syncScrollLock('close');
      if (!skipFocus) {
        var ob = root.querySelector('[data-cs-p02-open]') || lastFocus;
        if (ob && ob.focus) { ob.focus(); }
      }
    }

    /* ---- 事件（全部委派在 root，swap 之後不用重綁） ---- */
    root.addEventListener('click', function (e) {
      var t = e.target;
      var a = t.closest('[data-cs-p02-open]');
      if (a) { e.preventDefault(); openDrawer(a); return; }
      if (t.closest('[data-cs-p02-close]')) { e.preventDefault(); closeDrawer(); return; }
      var more = t.closest('[data-cs-more]');
      if (more) {
        var list = more.parentNode.querySelector('.cs-p02-f__list');
        if (list) {
          var on = list.classList.toggle('is-expanded');
          more.setAttribute('aria-expanded', on ? 'true' : 'false');
        }
        return;
      }
      var clr = t.closest('[data-cs-p02-clear]');
      if (clr) {
        e.preventDefault();
        var d = drawer();
        if (!d) { return; }
        d.querySelectorAll('.cs-p02-f__in:checked').forEach(function (c) { c.checked = false; });
        // 由其他 tag 路徑進來時，路徑上的 tag 是 hidden cs_tag；清除也要拿掉，不然套用後仍被那個 tag 限制
        d.querySelectorAll('input[name="cs_tag"]').forEach(function (h) { (d._csRemoved = d._csRemoved || []).push({ n: h, p: h.parentNode, s: h.nextSibling }); h.parentNode.removeChild(h); });
        d.querySelectorAll('cs-p02-price').forEach(function (p) { if (p.reset) { p.reset(); } });
        noteClear(d);
        var clrForm = d.querySelector('form');
        if (clrForm) { updateSel(clrForm); }
        schedulePreview(0);
        return;
      }
      var moreBtn = t.closest('[data-cs-p02-more]');
      if (moreBtn) {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) { return; }   // 新分頁開等：照原生連結
        var np = nextPageOf(moreBtn);
        if (np > 1 && piece('grid') && piece('grid').contains(moreBtn)) { e.preventDefault(); loadMore(moreBtn, np); }
        return;   // 讀不到頁碼：不攔，走原生整頁連結
      }
      var useRm = t.closest('[data-cs-p02-use-remove]');
      if (useRm && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
        e.preventDefault();
        var gone = useRm.getAttribute('data-cs-p02-use-remove');
        var left = tagsOfPath(window.location.pathname).filter(function (x) { return decodeSafe(x) !== gone; });
        var keepP = new URLSearchParams(window.location.search);
        setView(keepP);
        keepP.delete('page');
        cancelSchedule();
        apply(keepP, null, false, pathWithTags(left));
        return;
      }
      var tagRm = t.closest('[data-cs-p02-tag-remove]');
      if (tagRm && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
        /* r40 F3：非用途 tag 的 chip。拿掉表單裡對應的 hidden cs_tag，用 resolveForm 算新路徑再套用（其他篩選參數照表單保留）；
           算完把 input 放回原位（換格會整份換掉表單；套用被取消或失敗時表單維持原狀）。找不到表單或 input：改走用途 chip 的做法，從目前路徑拿掉那個 tag。 */
        e.preventDefault();
        var tagGone = tagRm.getAttribute('data-cs-p02-tag-remove');
        var tagForm = root.querySelector('form[data-cs-p02-form="d"]') || root.querySelector('form[data-cs-p02-form]');
        var tagHid = tagForm ? Array.prototype.filter.call(tagForm.querySelectorAll('input[name="cs_tag"]'), function (h) { return h.value === tagGone; })[0] : null;
        if (tagHid) {
          var tagRem = { n: tagHid, p: tagHid.parentNode, s: tagHid.nextSibling };
          tagHid.parentNode.removeChild(tagHid);
          var tagR;
          /* r40 Codex b：移除非用途 tag 不套用途重設規則（resetMode 傳 false；use_reset=true 時不可以連帶拿掉用途 tag） */
          try { tagR = resolveForm(tagForm, false, false); } finally { tagRem.p.insertBefore(tagRem.n, tagRem.s && tagRem.s.parentNode === tagRem.p ? tagRem.s : null); }
          cancelSchedule();
          apply(tagR.params, null, false, tagR.path);
        } else {
          var tagLeft = tagsOfPath(window.location.pathname).filter(function (x) { return decodeSafe(x) !== tagGone; });
          var tagP = new URLSearchParams(window.location.search);
          setView(tagP);
          tagP.delete('page');
          cancelSchedule();
          apply(tagP, null, false, pathWithTags(tagLeft));
        }
        return;
      }
      var link = t.closest('a[data-cs-p02-link]');
      if (link && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey && e.button === 0) {
        e.preventDefault();
        cancelSchedule();
        apply(paramsFromLink(link), null, false, new URL(link.getAttribute('href'), window.location.href).pathname);
      }
    });

    root.addEventListener('input', function (e) {
      var t = e.target;
      var form = t.form;
      if (!form) { return; }
      var kind = form.getAttribute('data-cs-p02-form');
      if (t.matches('.cs-p02-price__num')) {
        noteEdit(t);
        updateSel(form);
        if (kind === 'd') { schedule(form, 500, t.id, false, 'typing'); } else if (kind === 'm') { schedulePreview(500); }
      } else if (t.matches('.cs-p02-price__range')) {
        /* 拖動中只更新畫面，放開（change）才套用；但套用在途時要先記下數字，swap 若在放開前發生才接得回來（Codex r30b） */
        var pr = t.closest && t.closest('cs-p02-price');
        if (pr) { Array.prototype.forEach.call(pr.querySelectorAll('.cs-p02-price__num'), noteEdit); }
      }
    });

    root.addEventListener('change', function (e) {
      var t = e.target;
      /* r40（m205 第五次驗證）：swap 移除「客人改過值、正有焦點」的價錢框時，Chrome 會送 change（再送 focusout）。那不是失焦／Enter 收尾，
         不可以排 price-end（會把打字段結束、每停一次多一筆，還多發一個重複請求）。swap 前若還有打到一半的字，原本那個 'typing' debounce 仍在，會照常套用。 */
      if (swapping && t.matches && t.matches('.cs-p02-price__num')) { return; }
      if (t.matches && t.matches('[data-cs-p02-q]')) { return; }   // 第三十一輪第二批：搜尋框失焦也會送 change；抽屜裡的那個在 form 內，不擋的話會多發一次「查看 N 件」預覽請求
      var form = t.form;
      if (!form) { return; }
      var kind = form.getAttribute('data-cs-p02-form');
      var isUse = t.matches && t.matches('input[name="cs_use"]');
      if (t.matches('.cs-p02-f__in, .cs-p02-price__range, .cs-p02-price__num')) { window.setTimeout(function () { updateSel(form); }, 0); }
      if (isUse && t.checked) {
        // 用途一次只能選一個：兩個 tag 同時要求是「兩者都有」，不是「或」
        form.querySelectorAll('input[name="cs_use"]').forEach(function (o) { if (o !== t) { o.checked = false; } });
      }
      // 第三十輪：套用在途時記下抽屜裡的改動（滑桿沒有 name，記它那一組的價錢數字）
      var pc = t.closest && t.closest('cs-p02-price');
      if (pc) { Array.prototype.forEach.call(pc.querySelectorAll('.cs-p02-price__num'), noteEdit); } else { noteEdit(t); }
      if (kind === 'd') {
        if (t.matches('.cs-p02-price__range')) { schedule(form, 150, t.id); }
        else if (t.matches('.cs-p02-price__num')) { schedule(form, 0, t.id, false, 'price-end'); }   // r40：價錢框失焦／Enter＝這段打字收尾
        else if (t.matches('.cs-p02-f__in, select')) { schedule(form, 0, t.id, isUse); }
      } else if (kind === 'm') {
        schedulePreview(t.matches('.cs-p02-price__num') ? 500 : 250);
      }
    });

    /* Codex d：價錢框失焦就結束打字段，不只靠 change。換格後新的輸入框值已等於套用值，直接失焦不會有 change，
       不在這裡結段的話，之後回來再打會併進上一段。change 先於 focusout：值有變時 change 已排了 price-end（timerPending），由它收尾，這裡不清。 */
    root.addEventListener('focusout', function (e) {
      var t = e.target;
      if (!t.matches || !t.matches('.cs-p02-price__num') || !t.form || t.form.getAttribute('data-cs-p02-form') !== 'd') { return; }
      if (swapping) { return; }   // 換格把輸入框換掉造成的 focusout，不是客人離開
      // Codex e：同一組價錢的上下限之間 Tab，不算離開（同一次價錢編輯）
      var rt = e.relatedTarget;
      if (rt && rt.matches && rt.matches('.cs-p02-price__num') && rt.closest('cs-p02-price') === t.closest('cs-p02-price')) {
        // Codex f：值有變時 change 先到、已排了 price-end（delay 0，focusout 在同一個工作裡先跑）。上下限之間 Tab 仍是同一次編輯：改回打字段，不在這裡收尾
        if (timerPending && pendingMode === 'price-end') { schedule(t.form, 500, t.id, false, 'typing'); }
        return;
      }
      // Codex e：還有排著的打字 debounce、卻沒有 change（例如改回原值）：立刻改成 price-end 收尾，不讓它之後以打字段套用
      if (timerPending && pendingMode === 'typing') { schedule(t.form, 0, t.id, false, 'price-end'); return; }
      if (!timerPending) { typingSeg = ''; }
    });
    window.addEventListener('pageshow', function (ev) { if (ev.persisted) { typingSeg = ''; } });   // Codex d：bfcache 還原＝新的瀏覽，不接舊段

    root.addEventListener('submit', function (e) {
      var form = e.target;
      var kind = form.getAttribute && form.getAttribute('data-cs-p02-form');
      if (!kind) { return; }
      e.preventDefault();
      if (kind === 'm') {
        // pointerup 觸發與原生 click 觸發同一次按下時只做一次
        if (Date.now() - lastDrawerApply < 700) { return; }
        applyFromDrawer(form);
        return;
      }
      cancelSchedule();
      var r = resolveForm(form, false, USE_RESET);
      // r40 Codex c：在價錢框按 Enter（submit）＝這段打字收尾，跟失焦的 change 一樣帶 'price-end'
      var se = document.activeElement;
      apply(r.params, null, false, r.path, false, (se && se.matches && se.matches('.cs-p02-price__num') && form.contains(se)) ? 'price-end' : '');
    });

    /* S-B7：觸控時序保險。手機按「查看 N 件」前通常剛在價錢框輸入：按下的瞬間輸入框失焦、鍵盤收起、固定在底部的按鈕會位移，
       原生 click 可能落空（沒有 submit）。所以 (1) mousedown 不讓焦點離開輸入框，避免按下時才收鍵盤；
       (2) 在同一顆按鈕上 pointerdown→pointerup（位移 <10px）就直接套用，不等 click。 */
    var armed = null;
    root.addEventListener('mousedown', function (e) {
      if (e.target.closest && e.target.closest('[data-cs-p02-apply]')) { e.preventDefault(); }
    });
    root.addEventListener('pointerdown', function (e) {
      var b = e.target.closest && e.target.closest('[data-cs-p02-apply]');
      armed = b ? { btn: b, x: e.clientX, y: e.clientY, t: Date.now() } : null;
    });
    root.addEventListener('pointerup', function (e) {
      var a = armed;
      armed = null;
      if (!a || e.pointerType === 'mouse') { return; }
      var b = e.target.closest && e.target.closest('[data-cs-p02-apply]');
      if (b !== a.btn || Math.abs(e.clientX - a.x) > 10 || Math.abs(e.clientY - a.y) > 10 || Date.now() - a.t > 1500) { return; }
      var form = b.form;
      if (form && Date.now() - lastDrawerApply >= 700) { applyFromDrawer(form); }
    });

    root.addEventListener('keydown', function (e) {
      var d = drawer();
      if (!d || !d.classList.contains('is-open') || !d.contains(e.target)) { return; }
      if (e.key === 'Escape') { e.preventDefault(); closeDrawer(); return; }
      if (e.key !== 'Tab') { return; }
      var f = focusables(d);
      if (!f.length) { e.preventDefault(); return; }
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && (document.activeElement === first || document.activeElement === d)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    // 載入時網址已經帶抽屜錨點：CSS 的 :target 已把抽屜打開，這裡補上 .is-open 與焦點鎖，讓 Esc、關閉鍵、焦點回位都正常
    if (window.location.hash && drawer() && window.location.hash === '#' + drawer().id && !(DESKTOP.matches && hasSide())) { openDrawer(null); }

    // 斷點切換（兩個方向都對帳）：拉到桌面寬度時抽屜 display:none，先收掉再同步；拉回窄螢幕時抽屜是關著的，鎖也要是關著的
    var onWide = function () { if (DESKTOP.matches && hasSide()) { closeDrawer(true, 'resize'); } syncScrollLock('resize'); };
    if (DESKTOP.addEventListener) { DESKTOP.addEventListener('change', onWide); } else if (DESKTOP.addListener) { DESKTOP.addListener(onWide); }
    window.addEventListener('popstate', function () {
      syncScrollLock('popstate');
      /* r40 F1：上一頁／下一頁。網址（路徑＋search）跟最後一次換格的一樣＝只有 hash 變（例如抽屜的 #），什麼都不做；
         不一樣就用當時網址重抓並換格（fromPop：不再動歷史紀錄）。抽屜開著先走「不套用就關」（closeDrawer 非 applying → revertDrawer）。 */
      /* r40 Codex b：先清掉排隊中的價錢 debounce（不然舊表單會在返回後再套用一次、又 push 一筆）；
         網址回到 lastKey（例如 C→上一頁 B 還沒回來→下一頁 C）時，作廢在途的請求（B 回來不可以換格，失敗／逾時也不可以導向 B）。 */
      window.clearTimeout(timer); timerPending = false; pendingMode = '';
      typingSeg = '';   // r40 Codex c：返回時立刻結束價錢打字段（返回途中再打字要自成新段，不可以蓋掉返回的目的地）
      if (window.location.pathname + window.location.search === lastKey) {
        if (ctrl) { ctrl.abort(); ctrl = null; setBusy(false); }
        return;
      }
      var pd = drawer();
      if (pd && pd.classList.contains('is-open')) { closeDrawer(true, 'popstate'); }
      cancelSchedule();
      apply(new URLSearchParams(window.location.search), null, false, window.location.pathname, true);
    });

    // 第三十一輪第二批：搜尋框啟動（拿掉 hidden；瀏覽器若還原了輸入框的字就接起來）
    promoSync();   // 第三十一輪第三批戊：宣傳框日期後備（先於 qFromInputs：它會呼叫 qApply，搜尋字還在的話會再把宣傳框隱藏）
    document.addEventListener('cs-p02:swap', function () { promoSync(); qApply(); });   // 換格與看更多之後都複查（qApply 冪等）
    window.addEventListener('pageshow', function (ev) { if (ev.persisted) { promoSync(); qApply(); promoPlace(); } });
    document.addEventListener('cs-p02:swap', function () { promoPlace(); promoWatch(); });   // 換格（ul 換成新節點，要重新監看）與看更多之後重排；獨立一個監聽，不和上面的日期／搜尋複查混在一起
    promoPlace();   // 第三十一輪審查修正：載入時依實際欄數搬位置，並開始監看 ul 尺寸
    promoWatch();
    qReveal();
    qFromInputs();
  }

  function boot() { document.querySelectorAll('[data-cs-p02]').forEach(init); }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); } else { boot(); }
})();
